import * as Sentry from '@sentry/nextjs';

import { AppLogger } from '@/lib/logger';
import {
  sanitizeMutationMetadata,
  type MutationLogMetadata,
} from '@/lib/sanitize-mutation-metadata';

export type MutationLogPhase =
  | 'start'
  | 'success'
  | 'error'
  | 'translation-error';

export interface MutationLogOptions {
  metadata?: MutationLogMetadata;
  code?: string;
  error?: unknown;
}

type SentryBreadcrumbInput = Parameters<typeof Sentry.addBreadcrumb>[0];

const MUTATION_PHASE_MESSAGE: Record<MutationLogPhase, string> = {
  start: 'mutation:start',
  success: 'mutation:success',
  error: 'mutation:error',
  'translation-error': 'mutation:translation-error',
};

const MUTATION_PHASE_LEVEL: Record<
  MutationLogPhase,
  SentryBreadcrumbInput['level']
> = {
  start: 'info',
  success: 'info',
  error: 'error',
  'translation-error': 'warning',
};

function ensureError(value: unknown): Error {
  if (value instanceof Error) {
    return value;
  }
  if (typeof value === 'string') {
    return new Error(value);
  }
  return new Error('mutation_error');
}

function buildContext(
  phase: MutationLogPhase,
  metadata: MutationLogMetadata | undefined,
  code?: string
): MutationLogMetadata {
  const overrides: MutationLogMetadata = {
    status: phase,
  };

  if (code) {
    overrides.code = code;
  }

  if (phase === 'translation-error' && !overrides.code) {
    overrides.code = 'TRANSLATION_ERROR';
  }

  return sanitizeMutationMetadata(metadata, overrides);
}

/**
 * Logs a mutation lifecycle event (start/success/error/translation-error).
 *
 * **Note on Breadcrumbs:**
 * This function directly emits breadcrumbs because it's called from client-side
 * React Query hooks where our server-side `withBreadcrumbTracking` wrapper
 * pattern doesn't apply.
 *
 * Future consideration: Create a client-side breadcrumb wrapper for React Query
 * mutations that provides similar lifecycle tracking.
 *
 * @param phase - Mutation lifecycle phase
 * @param options - Mutation metadata, error, and code
 */
export function logMutationEvent(
  phase: MutationLogPhase,
  options: MutationLogOptions = {}
): void {
  const context = buildContext(phase, options.metadata, options.code);

  if (typeof Sentry.addBreadcrumb === 'function') {
    // NOTE: Direct breadcrumb emission from client-side mutation logger
    // This is acceptable as it runs in React Query context, not server routes
    const level = MUTATION_PHASE_LEVEL[phase];
    Sentry.addBreadcrumb({
      category: 'mutation',
      ...(level && { level }),
      message: MUTATION_PHASE_MESSAGE[phase],
      data: context,
    });
  }

  if (phase === 'error') {
    AppLogger.error('mutation:error', ensureError(options.error), context);
    return;
  }

  if (phase === 'translation-error') {
    AppLogger.warn('mutation:error-translation', context);
    return;
  }

  const message = phase === 'start' ? 'mutation:start' : 'mutation:success';
  AppLogger.info(message, context);
}
